import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Col, Form, Input, Row } from "antd"
import PropTypes from "prop-types"
import moment from "moment";
import { serviceSolutionRules } from "../../../../config/constants/serviceSolutionRulesConstants";
import { SelectCompany } from "../../../../components/services/forms/SelectCompany";
import { SelectContact } from "../../../../components/services/forms/SelectContact";
import { SelectServiceType } from "../../../../components/services/forms/SelectServiceType";
import { SelectHeadquarters } from "../../../../components/headquarters/SelectHeadquarters";
import { useDictionaryTranslation } from "../../../../hooks/useDictionaryTranslation";

export const FormEditSupportService = ({
  formName,
  serviceData,
  handleSubmit: onSubmit,
}) => {
  const [form] = Form.useForm();
  const { translate: t } = useDictionaryTranslation();
  const selectedCompany = Form.useWatch('assignedCompany', form);
  const selectedServiceType = Form.useWatch('serviceType', form);
  const selectedHeadquarter = Form.useWatch('headquarter', form);
  const serviceDate = Form.useWatch('serviceDate', form);
  const { accountModules, solutionId } = useSelector((state) => ({
    accountModules: state.auth.accountModules,
    solutionId: state.auth.accountData.solutionId,
  }));
  const currentSolutionRules = serviceSolutionRules[solutionId] ?? serviceSolutionRules.default;

  const checkServiceTypeAssignedToCompany = (serviceType, company) => {
    let isServiceTypeAssigned = company == null || company.isUsingAllServiceType;
    const companyServiceTypes = company?.servicesTypes;
    if (!isServiceTypeAssigned && companyServiceTypes?.length && serviceType) {
      isServiceTypeAssigned =
        company.isUsingAllServiceType ||
        companyServiceTypes.some(({ idServiceType }) => {
          return idServiceType === serviceType._id;
        });
    }
    return isServiceTypeAssigned;
  };

  const selectHeadquarter = (item) => {
    form.setFieldsValue({
      headquarter: item,
      serviceType: null,
      assignedCompany: null,
      selectedContact: null,
    });
  };

  const selectCompany = (item) => {
    const newData = {
      assignedCompany: item,
      selectedContact: null,
    };
    const isServiceTypeAssignedToCompany = checkServiceTypeAssignedToCompany(serviceData?.idTypeService?._id, item);
    if (!isServiceTypeAssignedToCompany) {
      newData.serviceType = null;
    }
    if (currentSolutionRules.useCompanyAddress && !accountModules.finalCustomers) {
      newData.address = {
        address: item?.address,
        markerCoords: item?.markerCoords,
      };
    }
    form.setFieldsValue(newData);
  };

  const validateTime = (_, time) => {
    if (serviceData?.start) {
      const currentServiceDate = moment(serviceData.start);
      if (serviceDate === currentServiceDate.format('YYYY-MM-DD') && time === currentServiceDate.format('HH:mm')) {
        return Promise.resolve();
      }
    }
    const today = moment();
    const todaysDate = today.format('YYYY-MM-DD');
    if (serviceDate > todaysDate) {
      return Promise.resolve();
    }
    const minTime = today.format('HH:mm');
    if (serviceDate === todaysDate && time > minTime) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t('minTimeError', { minTime: today.format('HH:mm') })));
  };

  const getMinDate = () => {
    if (!currentSolutionRules.checkMinDate) {
      return null;
    }
    const today = moment().format('YYYY-MM-DD');
    if (serviceData?.start) {
      const currentServiceDate = moment(serviceData.start).format('YYYY-MM-DD');
      if (serviceDate < today && serviceDate === currentServiceDate) {
        return currentServiceDate;
      }
    }
    return today;
  };

  const selectServiceType = (item) => {
    form.setFieldsValue({ serviceType: item });
  };

  const selectContact = (item) => {
    const newData = {};
    if (
      currentSolutionRules.useContactAddress &&
      (!currentSolutionRules.useFinalCustomerAddress || !accountModules.finalCustomers)
    ) {
      newData.address = {
        address: item?.address,
        markerCoords: item?.markerCoords,
      };
    }
    form.setFieldsValue(newData);
  };


  const setFormData = useCallback(() => {
    const formValues = {
      serviceName: serviceData?.name,
      headquarter: serviceData?.idHeadquarter,
      serviceType: serviceData?.idTypeService,
      serviceDate: serviceData?.start ? moment(serviceData.start).format('YYYY-MM-DD') : undefined,
      serviceTime: serviceData?.start ? moment(serviceData.start).format('HH:mm') : undefined,
      assignedCompany: serviceData?.idAssignedCompany,
      selectedContact: serviceData?.idContactWhoCalls,
      serviceDescription: serviceData?.title,
    }
    form.setFieldsValue(formValues);
  }, [serviceData, form,])

  useEffect(() => {
    setFormData();
  }, [setFormData, serviceData]);

  return (
    <>
      <Form
        name={formName}
        form={form}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Row>
          {currentSolutionRules.serviceName ? (
            <Col xs={24} md={12}>
              <Form.Item name="serviceName" label={t('serviceName')}>
                <Input placeholder={t('serviceNamePlaceholder')} />
              </Form.Item>
            </Col>
          ) : null}

          <Col xs={24} md={12}>
            <Form.Item rules={[{ required: true }]} name="headquarter" label={t('headquarter')}>
              <SelectHeadquarters
                value={selectedHeadquarter}
                onChange={selectHeadquarter}
                idsHeadquarters={serviceData?.idUser?.profile?.idsHeadquarters}
                useFilteredIds={Boolean(serviceData?.idUser?.profile?.idsHeadquarters?.length)}
                autoSelect={false}
              />
            </Form.Item>
          </Col>

          {serviceData?.idTypeService?.idApp? (
            <Col xs={24} md={12}>
              <Form.Item
                name="serviceType"
                rules={[{ message: t('serviceTypeRequired'), required: true }]}
                label={t('serviceType')}
              >
                <SelectServiceType
                  idHeadquarter={selectedHeadquarter?._id}
                  idCustomer={selectedCompany?._id}
                  idApp={serviceData?.idTypeService?.idApp}
                  onChange={selectServiceType}
                  autoSelect
                  selectedServiceType={selectedServiceType}
                />
              </Form.Item>
            </Col>


          ):null}


          <Col xs={12} md={6}>
            <Form.Item
              name="serviceDate"
              rules={(() => {
                const validators = [{ message: t('serviceDateRequired'), required: true }];
                if (currentSolutionRules.checkMinDate) {
                  validators.push({
                    required: true,
                    validator: (_, date) => {
                      const minDate = getMinDate();
                      if (date >= minDate) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('minDateError', { minDate })));
                    },
                  });
                }
                return validators;
              })()}
              label={t('serviceDate')}
            >
              <Input placeholder={t('serviceDatePlaceholder')} type="date" min={getMinDate()} />
            </Form.Item>
          </Col>

          <Col xs={12} md={6}>
            <Form.Item
              name="serviceTime"
              rules={(() => {
                const validators = [{ message: t('serviceTimeRequired'), required: true }];
                if (currentSolutionRules.checkMinDate) {
                  validators.push({
                    required: true,
                    validator: validateTime,
                  });
                }
                return validators;
              })()}
              label={t('serviceTime')}
              dependencies={['serviceDate']}
            >
              <Input placeholder={t('serviceTimePlaceholder')} type="time" />
            </Form.Item>
          </Col>

          {accountModules.customers ? (
            <>
              <Col xs={24} md={12} z>
                <Form.Item
                  name="assignedCompany"
                  rules={[{ message: t('customerRequired'), required: true }]}
                  label={t('customer')}
                  valuePropName="selectedCompany"
                >
                  <SelectCompany onChange={selectCompany} idHeadquarter={selectedHeadquarter?._id} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="selectedContact"
                  rules={[{ message: t('contactRequired'), required: true }]}
                  label={t('contact')}
                  valuePropName="selectedContact"
                >
                  <SelectContact
                    onChange={selectContact}
                    selectedCompanyId={selectedCompany?._id}
                    enabledActions={currentSolutionRules?.enabledActionsContact}
                    selectedContact={selectContact}
                  />
                </Form.Item>
              </Col>
            </>
          ) : null}
          <Col xs={24} md={24}>
            <Form.Item name="serviceDescription" label={t('serviceDescription')}>
              <Input.TextArea autoSize={{ minRows: 5 }} placeholder={t('serviceDescriptionPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  )

}

FormEditSupportService.propTypes = {
  serviceData: PropTypes.shape({
    start: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    reference: PropTypes.string,
    address: PropTypes.string,
    idUser: PropTypes.object,
    idTypeService: PropTypes.object,
    idAssignedCompany: PropTypes.object,
    selectedAsset: PropTypes.string,
    idContactWhoCalls: PropTypes.object,
    idFinalCustomer: PropTypes.object,
    collaborators: PropTypes.array,
    idHeadquarter: PropTypes.object,
  }),
  formName: PropTypes.string,
  handleSubmit: PropTypes.func,
}